/**
 * Money-flow Sankey data and mount contract (sites#178, sites#261).
 * money-flow.ts (the ECharts bundle) draws Candidate and Committee cards
 * itself and registers window.electinfoMoneyFlow so client-rendered pages
 * (vendor detail) can load /js/money-flow.js on demand and mount a card on
 * their own table, without bundling ECharts.
 */

export interface FlowNode {
  id: string;
  /** in/out: counterparties; mid: purpose group; ie-* sides: independent expenditures for/against a candidate. */
  side: 'in' | 'mid' | 'focal' | 'out' | 'ie-in' | 'ie-out' | 'ie-opp-in' | 'ie-opp-out';
  /** Group: a mid-column committee category (individual Top Committees). */
  kind: 'Committee' | 'Candidate' | 'Vendor' | 'Individual' | 'Purpose' | 'Group' | 'Other';
  entityId: string | null;
  fecId: string | null;
  name: string;
  party: string | null;
  affinityDem: number | null;
  affinityRep: number | null;
  amount: number | null;
  count: number | null;
  otherCount: number | null;
  /** Drawn size when it differs from amount (small flows widened to stay visible); tooltips show amount. */
  display?: number;
}

export interface FlowLink {
  source: string;
  target: string;
  amount: number;
  count: number | null;
  flow: 'transfer' | 'contribution' | 'ie' | 'ie-oppose' | 'payment';
  /** Drawn width when it differs from amount; tooltips show amount. */
  display?: number;
}

export interface FlowCard {
  section: HTMLElement;
  /** Heading the Sankey | Table toggle sits beside. */
  heading: HTMLElement;
  /** The table or list the Sankey stands in for; hidden, not removed, in Sankey view. */
  list: HTMLElement;
  nodes: FlowNode[];
  links: FlowLink[];
  /** Which column the counterparties are in: in = left of the focal node. */
  side: 'in' | 'out';
  /** Caption under the chart, shown in Sankey view only. */
  note?: string;
}

/** Sankey geometry shared by money-flow.ts and the flow builders that size flows to it. */
export const FLOW_LAYOUT = { top: 8, bottom: 8, nodeGap: 6, minLabelPx: 6, rowPx: 22 } as const;

/** Chart height for the tallest column's node count. */
export function flowChartHeight(rows: number): number {
  return Math.max(200, rows * FLOW_LAYOUT.rowPx + 40);
}

export interface MoneyFlowApi {
  /** Add the Sankey | Table toggle to one card; false when there is nothing to draw. */
  mount(card: FlowCard): boolean;
  /** Dispose a client-rendered card before replacing its contents (e.g. cycle selection). */
  unmount?(section: HTMLElement): void;
  /** Read-only diagnostics for chart sizing/column regression tests. Null before first draw. */
  inspect?(section: HTMLElement): { width: number; option: unknown } | null;
}

declare global {
  interface Window {
    electinfoMoneyFlow?: MoneyFlowApi;
  }
}
